import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, CheckCircle, Clock, Calendar, MapPin,
  Copy, Check, Star, Zap, Film, ExternalLink, X,
  ChevronDown, BarChart3,
} from 'lucide-react';
import { db, functions } from '../firebase';
import {
  collection, doc, getDocs, setDoc, updateDoc,
  addDoc, serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { CONVENTION_SEED, STATUS_CONFIG, generatePitchEmail } from '../conventionData';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr + 'T12:00:00') - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-5 flex items-center gap-4">
      <div className={`p-3 rounded-xl ${color} bg-opacity-10`}>
        <Icon size={20} className={color.replace('bg-', 'text-')} />
      </div>
      <div>
        <p className="text-2xl font-black text-white">{value}</p>
        <p className="text-xs tracking-widest uppercase text-neutral-500">{label}</p>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase border ${cfg.color} ${cfg.bg} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
}

function EventCard({ event, onOpen }) {
  const days = daysUntil(event.date);
  const urgent = days !== null && days <= 30 && days > 0;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="bg-neutral-900 border border-neutral-800 rounded-2xl p-5 hover:border-neutral-600 transition-colors cursor-pointer group"
      onClick={() => onOpen(event)}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-black text-white text-base leading-tight truncate group-hover:text-amber-400 transition-colors">
            {event.name}
          </h3>
          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            <span className="flex items-center gap-1 text-xs text-neutral-500">
              <MapPin size={11} /> {event.location}
            </span>
            <span className="flex items-center gap-1 text-xs text-neutral-500">
              <Calendar size={11} /> {formatDate(event.date)}
            </span>
            {urgent && (
              <span className="text-xs text-amber-400 font-semibold">
                {days}d away
              </span>
            )}
          </div>
        </div>
        <StatusBadge status={event.status} />
      </div>

      {/* Guests */}
      <div className="flex items-start gap-2 mb-4">
        <Star size={12} className="text-amber-400 mt-0.5 shrink-0" />
        <p className="text-xs text-neutral-400 leading-relaxed">
          {event.guests.join(', ')}
        </p>
      </div>

      {/* Notes preview */}
      {event.notes && (
        <p className="text-xs text-neutral-600 italic truncate mb-4">
          {event.notes}
        </p>
      )}

      <div className="flex items-center justify-between">
        <span className="text-xs text-neutral-600 group-hover:text-neutral-400 transition-colors">
          Click to manage →
        </span>
        {event.contactEmail && (
          <span className="text-xs text-neutral-500 truncate max-w-[160px]">
            {event.contactEmail}
          </span>
        )}
      </div>
    </motion.div>
  );
}

const sendPitchEmailFn = httpsCallable(functions, 'sendPitchEmail');

function EmailModal({ event, onClose, onStatusChange }) {
  const { subject: generatedSubject, body: generatedBody } = generatePitchEmail(event);
  const [editableSubject, setEditableSubject] = useState(generatedSubject);
  const [editableBody, setEditableBody] = useState(generatedBody);
  const [copied, setCopied] = useState(null); // 'subject' | 'body' | 'all'
  const [notes, setNotes] = useState(event.notes || '');
  const [contactEmail, setContactEmail] = useState(event.contactEmail || '');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null); // 'success' | 'error'

  async function copyText(text, key) {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  }

  const subject = editableSubject;
  const body = editableBody;

  async function handleStatusChange(newStatus) {
    await onStatusChange(event.id, newStatus, { notes, contactEmail });
  }

  async function saveNotes() {
    setSaving(true);
    await onStatusChange(event.id, event.status, { notes, contactEmail });
    setSaving(false);
  }

  async function handleSendPitch() {
    if (!contactEmail) return;
    setSending(true);
    setSendResult(null);
    try {
      await sendPitchEmailFn({ to: contactEmail, subject, body, eventName: event.name });
      setSendResult('success');
      await onStatusChange(event.id, 'sent', { notes, contactEmail });
      setTimeout(() => setSendResult(null), 4000);
    } catch (err) {
      console.error(err);
      setSendResult('error');
      setTimeout(() => setSendResult(null), 4000);
    }
    setSending(false);
  }

  const mailtoLink = `mailto:${contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="min-h-full max-w-3xl mx-auto px-4 sm:px-6 py-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-amber-400 font-medium">
              Outreach
            </span>
            <h2 className="text-2xl font-black mt-1">{event.name}</h2>
            <div className="flex items-center gap-3 mt-1.5">
              <span className="text-sm text-neutral-400">{formatDate(event.date)}</span>
              <span className="text-neutral-700">·</span>
              <span className="text-sm text-neutral-400">{event.location}</span>
              <StatusBadge status={event.status} />
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 p-2 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Status actions */}
        <div className="flex flex-wrap gap-2 mb-6">
          {['pending','sent','booked','declined'].map((s) => {
            const cfg = STATUS_CONFIG[s];
            const isActive = event.status === s;
            return (
              <button
                key={s}
                onClick={() => handleStatusChange(s)}
                className={`px-4 py-2 rounded-full text-xs font-bold tracking-widest uppercase border transition-all ${
                  isActive
                    ? `${cfg.color} ${cfg.bg} ${cfg.border}`
                    : 'border-neutral-700 text-neutral-500 hover:border-neutral-500 hover:text-neutral-300'
                }`}
              >
                {isActive && <span className="mr-1">✓</span>}
                {cfg.label}
              </button>
            );
          })}
        </div>

        {/* Contact + Notes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-xs text-neutral-500 uppercase tracking-widest mb-1.5">
              Contact Email
            </label>
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="booker@convention.com"
              className="w-full bg-neutral-900 border border-neutral-700 rounded-xl px-3 py-2.5 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-amber-400 transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs text-neutral-500 uppercase tracking-widest mb-1.5">
              Notes
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any notes about this event..."
              className="w-full bg-neutral-900 border border-neutral-700 rounded-xl px-3 py-2.5 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-amber-400 transition-colors"
            />
          </div>
        </div>
        <button
          onClick={saveNotes}
          disabled={saving}
          className="mb-6 text-xs text-neutral-500 hover:text-amber-400 transition-colors disabled:opacity-40"
        >
          {saving ? 'Saving…' : '↑ Save contact & notes'}
        </button>

        {/* Guest list */}
        <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-4 mb-6">
          <p className="text-xs tracking-widest uppercase text-neutral-500 mb-2">Confirmed Guests</p>
          <div className="flex flex-wrap gap-2">
            {event.guests.map((g) => (
              <span key={g} className="flex items-center gap-1.5 px-3 py-1 bg-amber-400/10 border border-amber-400/20 rounded-full text-xs text-amber-300 font-medium">
                <Star size={10} /> {g}
              </span>
            ))}
          </div>
        </div>

        {/* Email generator */}
        <div className="border border-neutral-800 rounded-2xl overflow-hidden">
          <div className="bg-neutral-900 px-5 py-3 flex items-center justify-between border-b border-neutral-800">
            <div className="flex items-center gap-2">
              <Mail size={15} className="text-amber-400" />
              <span className="text-sm font-bold">Generated Pitch Email</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => copyText(`Subject: ${subject}\n\n${body}`, 'all')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-neutral-800 hover:bg-neutral-700 rounded-lg text-xs font-medium transition-colors"
              >
                {copied === 'all' ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
                {copied === 'all' ? 'Copied!' : 'Copy All'}
              </button>
              {contactEmail && (
                <a
                  href={mailtoLink}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-neutral-800 hover:bg-neutral-700 text-white rounded-lg text-xs font-medium transition-colors"
                >
                  <ExternalLink size={13} /> Open in Mail
                </a>
              )}
              {contactEmail && (
                <button
                  onClick={handleSendPitch}
                  disabled={sending}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold transition-colors disabled:opacity-50 ${
                    sendResult === 'success'
                      ? 'bg-emerald-500 text-white'
                      : sendResult === 'error'
                      ? 'bg-red-500 text-white'
                      : 'bg-amber-400 hover:bg-amber-300 text-black'
                  }`}
                >
                  {sending ? (
                    <><Clock size={13} className="animate-spin" /> Sending…</>
                  ) : sendResult === 'success' ? (
                    <><Check size={13} /> Sent!</>
                  ) : sendResult === 'error' ? (
                    <>Failed — retry</>
                  ) : (
                    <><Mail size={13} /> Send Pitch</>
                  )}
                </button>
              )}
              {!contactEmail && (
                <span className="text-xs text-neutral-600 italic">Add contact email to send</span>
              )}
            </div>
          </div>

          {/* Subject */}
          <div className="px-5 py-3 bg-neutral-950 border-b border-neutral-800">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <span className="text-xs text-neutral-500 uppercase tracking-widest">Subject</span>
                <input
                  type="text"
                  value={editableSubject}
                  onChange={(e) => setEditableSubject(e.target.value)}
                  className="w-full bg-transparent text-sm text-white mt-0.5 focus:outline-none focus:text-amber-100 placeholder-neutral-600"
                />
              </div>
              <button
                onClick={() => copyText(subject, 'subject')}
                className="shrink-0 p-1.5 text-neutral-500 hover:text-white transition-colors"
              >
                {copied === 'subject' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="relative">
            <textarea
              value={editableBody}
              onChange={(e) => setEditableBody(e.target.value)}
              className="w-full px-5 py-5 text-sm text-neutral-300 leading-relaxed bg-transparent focus:outline-none resize-none max-h-[420px] overflow-y-auto font-sans"
              rows={editableBody.split('\n').length + 1}
            />
            <button
              onClick={() => copyText(body, 'body')}
              className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 bg-neutral-800/90 hover:bg-neutral-700 rounded-lg text-xs font-medium transition-colors backdrop-blur-sm"
            >
              {copied === 'body' ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
              {copied === 'body' ? 'Copied!' : 'Copy Body'}
            </button>
          </div>
        </div>

        {/* Deliverables reminder */}
        <div className="mt-4 grid grid-cols-3 gap-3">
          {[
            { icon: Film, label: '60s Hero Reel', sub: '6K · DaVinci' },
            { icon: Zap, label: '3–5 Social Clips', sub: 'TikTok / Reels' },
            { icon: Clock, label: '48hr Turnaround', sub: 'Hype clips' },
          ].map(({ icon: Icon, label, sub }) => (
            <div key={label} className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-3 text-center">
              <Icon size={16} className="text-amber-400 mx-auto mb-1" />
              <p className="text-xs font-bold text-white">{label}</p>
              <p className="text-xs text-neutral-500">{sub}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

const FILTERS = ['all', 'pending', 'sent', 'booked', 'declined'];

export default function OutreachDashboard() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [activeEvent, setActiveEvent] = useState(null);
  const [sortBy, setSortBy] = useState('date'); // 'date' | 'name'

  // Load + sync from Firestore.
  // Always upserts seed data by ID so new events appear without wiping saved statuses.
  useEffect(() => {
    async function load() {
      try {
        const ref = collection(db, 'outreachEvents');
        const snap = await getDocs(ref);
        const existing = {};
        snap.docs.forEach((d) => { existing[d.id] = d.data(); });

        // Upsert: add new events, preserve status/notes/contactEmail on existing ones
        await Promise.all(
          CONVENTION_SEED.map((e) => {
            const saved = existing[e.id];
            return setDoc(doc(db, 'outreachEvents', e.id), {
              ...e,
              // Preserve any fields the user has already set
              status: saved?.status ?? e.status,
              notes: saved?.notes ?? e.notes,
              contactEmail: saved?.contactEmail ?? e.contactEmail,
              createdAt: saved?.createdAt ?? serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
          })
        );

        // Reload after upsert
        const fresh = await getDocs(ref);
        const loaded = fresh.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEvents(loaded.sort((a, b) => (a.date || '').localeCompare(b.date || '')));
      } catch (err) {
        console.error('Firestore load error:', err);
        setLoadError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleStatusChange(eventId, newStatus, extra = {}) {
    const ref = doc(db, 'outreachEvents', eventId);
    const update = { status: newStatus, updatedAt: serverTimestamp(), ...extra };
    await updateDoc(ref, update);

    // Log the action
    await addDoc(collection(db, 'outreach_logs'), {
      eventId,
      action: newStatus,
      ...extra,
      timestamp: serverTimestamp(),
    });

    setEvents((prev) =>
      prev.map((e) => (e.id === eventId ? { ...e, status: newStatus, ...extra } : e))
    );

    // Keep modal in sync
    if (activeEvent?.id === eventId) {
      setActiveEvent((prev) => ({ ...prev, status: newStatus, ...extra }));
    }
  }

  const counts = events.reduce((acc, e) => {
    acc[e.status] = (acc[e.status] || 0) + 1;
    return acc;
  }, {});

  const filtered = events
    .filter((e) => filter === 'all' || e.status === filter)
    .sort((a, b) => {
      if (sortBy === 'date') return (a.date || '').localeCompare(b.date || '');
      return a.name.localeCompare(b.name);
    });

  return (
    <div className="min-h-screen bg-black text-white pt-16 sm:pt-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">

        {/* Header */}
        <div className="mb-10">
          <span className="text-xs tracking-[0.3em] uppercase text-amber-400 font-medium">
            Black Magic Films
          </span>
          <h1 className="text-4xl sm:text-5xl font-black mt-2 mb-2">
            Convention Outreach
          </h1>
          <p className="text-neutral-400 max-w-xl">
            Track media partnership pitches to major 2026 conventions. Generate personalized email drafts and log outreach status.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
          <StatCard label="Total Events"  value={events.length}           color="bg-neutral-400" icon={BarChart3}    />
          <StatCard label="Pending"       value={counts.pending  || 0}    color="bg-amber-400"   icon={Clock}        />
          <StatCard label="Sent"          value={counts.sent     || 0}    color="bg-blue-400"    icon={Mail}         />
          <StatCard label="Booked"        value={counts.booked   || 0}    color="bg-emerald-400" icon={CheckCircle}  />
        </div>

        {/* Filters + Sort */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            {FILTERS.map((f) => {
              const cfg = f !== 'all' ? STATUS_CONFIG[f] : null;
              const isActive = filter === f;
              return (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase border transition-all ${
                    isActive
                      ? cfg
                        ? `${cfg.color} ${cfg.bg} ${cfg.border}`
                        : 'bg-white text-black border-white'
                      : 'border-neutral-700 text-neutral-500 hover:border-neutral-500 hover:text-white'
                  }`}
                >
                  {f === 'all' ? `All (${events.length})` : `${STATUS_CONFIG[f].label} (${counts[f] || 0})`}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setSortBy((s) => (s === 'date' ? 'name' : 'date'))}
            className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-white transition-colors"
          >
            <ChevronDown size={14} />
            Sort by {sortBy === 'date' ? 'date' : 'name'}
          </button>
        </div>

        {/* Event grid */}
        {loading ? (
          <div className="text-center py-20 text-neutral-600">Loading events…</div>
        ) : loadError ? (
          <div className="text-center py-20">
            <p className="text-red-400 font-bold mb-2">Failed to load events</p>
            <p className="text-neutral-500 text-sm font-mono">{loadError}</p>
            <p className="text-neutral-600 text-xs mt-4">Check that Firestore rules are deployed:<br/><code>firebase deploy --only firestore</code></p>
          </div>
        ) : (
          <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence>
              {filtered.map((event) => (
                <EventCard key={event.id} event={event} onOpen={setActiveEvent} />
              ))}
            </AnimatePresence>
            {filtered.length === 0 && (
              <div className="col-span-3 text-center py-16 text-neutral-600">
                No events with status "{filter}"
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Email / detail modal */}
      <AnimatePresence>
        {activeEvent && (
          <EmailModal
            event={activeEvent}
            onClose={() => setActiveEvent(null)}
            onStatusChange={handleStatusChange}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
